import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {BidExtendedModel, Plug, PlugsView} from "../bidding_model";
import {Cell} from "../../project_conf/bidConfig_model";
import {SavePdfButton} from "../SavePdfButton";
import {SmartFeedbackRow} from "../../feedback/SmartFeedback";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "../../ui/select";
import {Input} from "../../ui/input";
import {Button} from "../../ui/button";
import {useToast} from "../../ui/use-toast";
import {postWithToken} from "../../../api/apiBase";
import {USDonChangeCents, USDtoValueCents} from "../../fields/USDField";
import LinkInfo from "./linkInfo";
import {PlugCell} from "../../developer/PlugCell";
import {plugSum, signedMoney} from "../../developer/plugs";
import {moneyFormatter} from "../../../lib/formatters";
import {BidLevelingState} from "../../developer/ai_leveling";


type DeveloperBidCardProps = {
    bid: BidExtendedModel;
    sheetData: Cell[][] | null;
    // leveling - owned by BidPage so the SOV plugs and this card agree
    plugs: Plug[];
    leveled: number | null;
    onPlugsChanged: (view: PlugsView) => void;
    // $ values the developer typed on developer only lines
    devLines: number;
    // AI leveling - owned by BidPage so the SOV's suggestion lines and this card agree
    leveling: BidLevelingState;
    // the SOV with plugs and suggestions - the AI review popup shows it beside the review
    reviewSov: React.ReactNode;
}


/**
 * Developer only - for a submitted bid:
 *   row 1: leveled amount, response status, download pdf
 *   row 2: AI review - the bid on its own (smart feedback v3) and against the other bids (ai leveling),
 *          one generate for both
 *
 * once a bid has plugs or developer only line values, the leveled amount is
 * base bid + plugs + developer lines and can't be typed in.
 * */
export function DeveloperBidCard({bid, sheetData, plugs, leveled, onPlugsChanged, devLines, leveling, reviewSov}: DeveloperBidCardProps) {
  const {accessToken} = useAuthInfo();
  const { project_id, bid_id } = useParams<string>();
  const { toast } = useToast();
  const [responseStatus, setResponseStatus] = useState(
      bid.response_status===null
      ? undefined : bid.response_status
  )

  // what feedback / ranking is computed on
  const [leveledAmount, setLeveledAmount] = useState<string>(
      bid.developer?.leveled_amount?.toString() ?? ""
  )
  const [savedLeveledAmount, setSavedLeveledAmount] = useState<string>(leveledAmount)

  // a plug write changed it - eg the last plug was deleted and the input is back
  useEffect(() => {
    if (leveled !== null) {
      setLeveledAmount(leveled.toString());
      setSavedLeveledAmount(leveled.toString());
    }
  }, [leveled]);

  const onSetResponseStatus = (resp_status: any) => {
    setResponseStatus(resp_status);
    postWithToken(
      `/projects/${project_id}/bidding/${bid_id}/developer_update`,
      JSON.stringify({ response_status: resp_status }),
      accessToken as string
    )
      .then((data) => {
        toast({
          title: "Success!",
          description: "Status updated successfully.",
          variant: "default",
        });
        setResponseStatus(resp_status);
      })
      .catch((error) => {
        console.error("Error submitting bid:", error);
        toast({
          title: "Error",
          description: JSON.stringify(error) || "Failed to udpate status.",
          variant: "destructive",
        });
      });
  };

  const onSaveLeveledAmount = () => {
    // nothing typed or unchanged - don't post
    if (leveledAmount === "" || leveledAmount === savedLeveledAmount) return;
    const value = Number(leveledAmount);
    postWithToken(
      `/projects/${project_id}/bidding/${bid_id}/developer_update`,
      JSON.stringify({ developer: { leveled_amount: value } }),
      accessToken as string
    )
      .then((data) => {
        toast({
          title: "Success!",
          description: "Leveled amount updated.",
          variant: "default",
        });
        setLeveledAmount(value.toString());
        setSavedLeveledAmount(value.toString());
      })
      .catch((error) => {
        console.error("Error updating leveled amount:", error);
        toast({
          title: "Error",
          description: JSON.stringify(error) || "Failed to update leveled amount.",
          variant: "destructive",
        });
      });
  };

  return (
      <Card>
        <CardHeader>

        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Leveled Amount</span>
              {plugs.length > 0 || devLines !== 0
                ? <span className="text-sm whitespace-nowrap">
                    <span className="text-gray-500">
                      {moneyFormatter.format(bid.bid_amount)}
                      {plugs.length > 0 && <> {signedMoney(plugSum(plugs))} plugs</>}
                      {devLines !== 0 && <> {signedMoney(devLines)} developer lines</>}
                      {" "}={" "}
                    </span>
                    <span className="font-medium">{moneyFormatter.format(leveled ?? bid.bid_amount)}</span>
                  </span>
                : <Input
                    className="w-[180px] font-normal"
                    placeholder=""
                    value={USDtoValueCents(leveledAmount)}
                    onChange={(e) => setLeveledAmount(USDonChangeCents(e.target.value))}
                    onBlur={onSaveLeveledAmount}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onSaveLeveledAmount();
                      }
                    }}
                  />}
              {/* base bid plugs - leveling that isn't on an SOV line */}
              <PlugCell
                project_id={project_id as string}
                bid_id={bid_id as string}
                cell_id={null}
                plugs={plugs.filter((p) => p.cell_id == null)}
                subValue={bid.bid_amount}
                onChange={onPlugsChanged}/>
              {bid.developer && <LinkInfo developer={bid.developer}/>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Response</span>
              <Select
                  onValueChange={onSetResponseStatus}
                  value={responseStatus }>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Set Response" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                    {/*<SelectItem value="revise_resubmit">Revise and Resubmit</SelectItem>*/}
                  <SelectItem value="validated">Validated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              {/* a link - ctrl click keeps the bid open. scope bids open the scope's compare tab */}
              <Button variant="outline" asChild>
                <Link to={bid.scope_id
                    ? `/project/${project_id}/scopes/${bid.scope_id}?tab=compare`
                    : `/project/${project_id}/bidding/compare`}>
                  Compare bids
                </Link>
              </Button>
              <SavePdfButton bid={bid} sheetData={sheetData}/>
            </div>
          </div>

          <div className="border-t pt-4">
            <SmartFeedbackRow project_id={project_id as string} bid_id={bid_id as string}
                              leveling={leveling} onPlugsChanged={onPlugsChanged}
                              sov={reviewSov} title={bid.company_name}
                              bidFolderId={bid.root?.id} scopeId={bid.scope_id}/>
          </div>
        </CardContent>
      </Card>
  );
}
